(() => {
  if (window.vfdShipperAccount) return;
  const config = document.getElementById('vfd-shipper-config');
  if (!config) return;
  const options = JSON.parse(config.textContent);
  const base = window.Shopify?.routes?.root || '/';
  const keys = ['Shipper', 'Account Number', 'Service'];
  let state = { selected: false, values: ['', '', ''] };
  let ready = false, revision = 0, savedRevision = -1, saving = false, checking = false;
  let error = '', timer, pending = Promise.resolve();
  const panels = () => [...document.querySelectorAll('[data-shipper-panel]')];
  const valid = () => !state.selected || state.values.every(value => value.trim());
  const flag = item => item.sku === 'SHIPPER-FLAG' || String(item.variant_id || item.id) === String(options.flagVariant) || item.properties?._shipper_flag === '1';
  const selected = cart => cart.items.some(flag) || [cart.attributes?.['Use Shipper Account'], cart.attributes?.['Use Shipper Account?']].some(value => String(value).toLowerCase() === 'yes');
  async function request(path, body) {
    const response = await fetch(base + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      signal: AbortSignal.timeout(15000), ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!response.ok) throw new Error(options.saveError);
    const result = await response.json();
    if (result.errors || result.status >= 400) throw new Error(options.saveError);
    return result;
  }
  function render() {
    document.documentElement.classList.toggle('shipper-ready', ready);
    document.documentElement.classList.toggle('shipper-accelerated-blocked', !ready || state.selected || saving || checking || savedRevision !== revision || !!error);
    for (const panel of panels()) {
      const toggle = panel.querySelector('[data-shipper-toggle]');
      toggle.checked = state.selected;
      toggle.disabled = !ready || checking;
      panel.querySelector('[data-shipper-no]').disabled = state.selected;
      panel.querySelector('[data-shipper-fields]').hidden = !state.selected;
      panel.querySelectorAll('[data-shipper-value]').forEach((input, index) => {
        if (input.value !== state.values[index]) input.value = state.values[index];
        input.required = state.selected;
        input.disabled = !ready || checking;
        input.setCustomValidity(state.selected && !input.value.trim() ? options.required : '');
      });
      const warning = panel.querySelector('[data-shipper-warning]');
      const message = error || (state.selected && !valid() ? options.required : '');
      if (warning.textContent !== message) warning.textContent = message;
      warning.hidden = !message;
      if (state.selected) panel.open = true;
    }
    document.querySelectorAll('#checkout, #CartDrawer-Checkout, #cart-notification-form [name="checkout"]').forEach(button => {
      button.disabled = !ready || checking;
      button.setAttribute('aria-busy', String(checking));
    });
  }
  function showError(message, focus = false) {
    error = message;
    render();
    const panel = panels().find(element => element.getClientRects().length && (!element.closest('cart-drawer') || element.closest('cart-drawer').classList.contains('active')));
    if (panel) {
      panel.open = true;
      if (focus) {
        const input = [...panel.querySelectorAll('[data-shipper-value]')].find(element => !element.value.trim());
        (input || panel.querySelector('[data-shipper-warning]')).focus();
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    } else if (focus) window.location.assign(base + 'cart');
  }
  async function persist(snapshot) {
    const attributes = { 'Use Shipper Account': snapshot.selected ? 'yes' : 'no', 'Use Shipper Account?': '' };
    keys.forEach((key, index) => attributes[key] = snapshot.selected ? snapshot.values[index].trim() : '');
    let cart = await request('cart/update.js', { attributes });
    const needsFlag = snapshot.selected && snapshot.values.every(value => value.trim());
    const flags = cart.items.filter(flag);
    if (needsFlag && !options.flagVariant) throw new Error(options.saveError);
    if (!needsFlag || flags.length !== 1 || flags[0]?.quantity !== 1) {
      for (const item of flags) await request('cart/change.js', { id: item.key, quantity: 0 });
      if (needsFlag) await request('cart/add.js', { items: [{ id: options.flagVariant, quantity: 1, properties: { _shipper_flag: '1' } }] });
    }
    cart = await request('cart.js');
    if (Object.entries(attributes).some(([key, value]) => (cart.attributes[key] || '') !== value)) throw new Error(options.saveError);
    const verifiedFlags = cart.items.filter(flag);
    if (needsFlag ? verifiedFlags.length !== 1 || verifiedFlags[0].quantity !== 1 : verifiedFlags.length !== 0) throw new Error(options.saveError);
    return cart;
  }
  function save() {
    clearTimeout(timer);
    const targetRevision = revision;
    const snapshot = { selected: state.selected, values: [...state.values] };
    saving = true;
    render();
    const task = pending.catch(() => {}).then(async () => {
      try {
        const cart = await persist(snapshot);
        if (revision === targetRevision) { savedRevision = targetRevision; error = ''; }
        return cart;
      } catch (failure) {
        showError(options.saveError);
        throw failure;
      } finally {
        if (revision === targetRevision) saving = false;
        render();
      }
    });
    pending = task;
    return task;
  }
  async function checkout(destination = base + 'checkout') {
    if (checking) return;
    if (!ready) { showError(options.saveError, true); return; }
    if (!valid()) { showError(options.required, true); return; }
    checking = true;
    error = '';
    render();
    try {
      const cart = await save();
      if (!cart.items.some(item => !flag(item))) throw new Error('empty');
      window.location.assign(destination);
    } catch (failure) {
      checking = false;
      saving = false;
      showError(options.saveError, true);
    }
  }
  document.addEventListener('input', event => {
    const input = event.target.closest('[data-shipper-value]');
    if (!input || !ready || checking) return;
    state.values[Number(input.dataset.shipperValue)] = input.value;
    revision++;
    error = '';
    render();
    clearTimeout(timer);
    timer = setTimeout(() => save().catch(() => {}), 400);
  }, true);
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-shipper-toggle]') || !ready || checking) return;
    state.selected = event.target.checked;
    revision++;
    error = '';
    render();
    save().catch(() => {});
  }, true);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('#checkout, #CartDrawer-Checkout, button[name="checkout"], input[name="checkout"], a[href]');
    if (!trigger) return;
    const href = trigger.getAttribute('href');
    const url = href ? new URL(href, location.href) : null;
    const checkoutLink = url?.origin === location.origin && /\/(checkout|checkouts)(\/|$)/.test(url.pathname);
    const cartLink = url?.origin === location.origin && url.pathname === base + 'cart';
    if (href && !checkoutLink && !(cartLink && (saving || savedRevision !== revision))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (cartLink) {
      if (!ready) { showError(options.saveError, true); return; }
      save().then(() => window.location.assign(url.href)).catch(() => showError(options.saveError, true));
    } else checkout();
  }, true);
  document.addEventListener('submit', event => {
    if (!['cart', 'CartDrawer-Form', 'cart-notification-form'].includes(event.target.id)) return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout();
  }, true);
  document.addEventListener('shopify:section:load', render);
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-shipper-panel], [data-shipper-checkout]') || node.querySelector?.('[data-shipper-panel], [data-shipper-checkout]'))))) render();
  }).observe(document.body, { childList: true, subtree: true });
  window.vfdShipperAccount = { checkout };
  function initialize() {
    ready = false;
    checking = false;
    render();
    return request('cart.js').then(cart => {
      state = { selected: selected(cart), values: keys.map(key => cart.attributes[key] || '') };
      ready = true;
      revision = 0;
      savedRevision = 0;
      saving = false;
      error = '';
      render();
    }).catch(() => showError(options.saveError));
  }
  window.addEventListener('pageshow', event => {
    if (event.persisted) initialize();
  });
  initialize();
})();
